import { computed, type ComputedRef, type Ref } from 'vue';
import * as Highcharts from 'highcharts';
import { Options } from 'highcharts';
import merge from 'lodash-es/merge';
import { parseISO } from 'date-fns';
import useColumnChartOptions from '@/components/charts/composables/useColumnChartOptions';
import { withAlpha } from '@/plugins/highcharts/chartColors';
import { i18n } from '@/plugins/internationalization/i18n';
import { DAY_MS, toDateString, weekInterval, type RecordWeekBin } from './useRecordsAggregation';

/** Id of the band drawn behind the hovered column, so it can be replaced without a redraw. */
const HOVER_BAND_ID = 'records-hover-band';

/** How far a non-selected bar fades once a week is selected. */
export const UNSELECTED_OPACITY = 0.4;

/** Alpha of the full-height band drawn behind the hovered column. */
const HOVER_BAND_ALPHA = 0.12;

export const CHART_HEIGHT = 340;

export type RecordsChartConfig = {
    /** Hex of the series' own colour, from the `chart` token block. One per records page. */
    color: Ref<string>;
    /** What the bars count, in this page's own words. Translated by the caller. */
    title: Ref<string>;
    /** Legend-free series name, shown in the tooltip. Translated by the caller. */
    seriesName: Ref<string>;
    /** More about a week, put after its value in the tooltip (" · 120 animals"); nothing when absent or null. Translated by the caller. */
    pointDetail?: (weekStart: string) => string | null;
    onBinClicked: (weekStart: string) => void;
};

/**
 * Takes the hover band off a chart.
 *
 * Bands are applied straight to the axis rather than through the options object: the chart
 * component deep-watches its options and calls `chart.update()` on any change, which would
 * re-render and re-animate the whole plot on every pointer move across the bars. A redraw drops an
 * imperatively added band, so the chart clears its own on update and the next pointer move puts a
 * fresh one back.
 */
export function clearHoverBand(chart: Highcharts.Chart | null | undefined) {
    chart?.xAxis?.[0]?.removePlotBand(HOVER_BAND_ID);
}

/** Puts the band behind the hovered column, replacing whatever band the chart already carries. */
export function setHoverBand(chart: Highcharts.Chart | null | undefined, timestamp: number, color: string) {
    const axis = chart?.xAxis?.[0];

    if (!axis) {
        return;
    }

    axis.removePlotBand(HOVER_BAND_ID);
    axis.addPlotBand({
        id: HOVER_BAND_ID,
        from: timestamp - 3.5 * DAY_MS,
        to: timestamp + 3.5 * DAY_MS,
        color: withAlpha(color, HOVER_BAND_ALPHA),
        zIndex: 0,
    });
}

/**
 * The line naming the week a bar belongs to, at the top of its tooltip.
 *
 * `%Z` is the app's own week.year token, the same one the axis labels resolve to. The days it covers
 * are spelled out beside it, in the wording the table subtitle uses for the same selection.
 */
export function weekTooltipHeader(timestamp: number): string {
    const [from, to] = weekInterval(toDateString(timestamp));

    return `<span style="font-size: 10px">${Highcharts.dateFormat('%Z', timestamp)} · ${i18n.global.d(parseISO(from), 'short')} - ${i18n.global.d(parseISO(to), 'short')}</span><br/>`;
}

/**
 * The weekly column chart every records page draws above its table, in the page's own colour.
 *
 * The x values are UTC midnights the server already chose as week boundaries, which is what lets
 * the global `[week].[year]` axis formatter — it reads UTC parts — agree with the bin the row
 * actually belongs to. The period picker's own bounds are deliberately dropped from the axis: they
 * are local midnights, and mixing the two shifts every label for anyone west of UTC.
 */
export function useRecordsChartOptions(bins: Ref<RecordWeekBin[]>, selectedWeekStart: Ref<string | null>, config: RecordsChartConfig): ComputedRef<Options> {
    /*
     * Static defaults only, taken once. The shared composable lodash-merges whatever it is handed
     * straight into its options object, so handing it a `computed` merges that ref's own internals
     * (`__v_isRef`, `_value`) in too and the result reads back as a ref holding nothing. The
     * week-dependent half is merged on top per render below, as the other charts here do it.
     */
    const columnDefaults = useColumnChartOptions({});

    const seriesData = computed(() =>
        bins.value.map((bin) => ({
            x: bin.timestamp,
            y: bin.number,
            color: selectedWeekStart.value !== null && selectedWeekStart.value !== bin.weekStart ? withAlpha(config.color.value, UNSELECTED_OPACITY) : config.color.value,
        }))
    );

    return computed(() => {
        const base = merge({}, columnDefaults.value) as Options;

        return merge(base, {
            chart: {
                height: CHART_HEIGHT,
            },
            /*
             * Left, on the same line as the chart card's own action row - reset and expand - which
             * the shared chart component overlays on the top-right corner, as the other titled
             * charts in the app read. The room the title takes is also what keeps those controls
             * off the y-axis, whose labels are on the right here, so no extra top spacing is set.
             */
            title: {
                text: config.title.value,
            },
            xAxis: {
                min: null,
                max: null,
            },
            yAxis: {
                min: 0,
                allowDecimals: false,
            },
            legend: {
                enabled: false,
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    borderWidth: 0,
                    borderRadius: 2,
                    pointPadding: 0.05,
                    groupPadding: 0.12,
                },
                series: {
                    // Highcharts dims every other series member while one is hovered, which fights
                    // the fade that marks a bar as unselected.
                    states: { inactive: { opacity: 1 } },
                    point: {
                        events: {
                            click() {
                                config.onBinClicked(toDateString((this as unknown as Highcharts.Point).x));
                            },
                            mouseOver() {
                                const point = this as unknown as Highcharts.Point;

                                setHoverBand(point.series?.chart, point.x, config.color.value);
                            },
                            mouseOut() {
                                const point = this as unknown as Highcharts.Point;

                                clearHoverBand(point.series?.chart);
                            },
                        },
                    },
                    tooltip: {
                        headerFormat: '',
                        pointFormatter() {
                            const point = this as unknown as Highcharts.Point;

                            const detail = config.pointDetail?.(toDateString(point.x)) ?? null;

                            return `${weekTooltipHeader(point.x)}<span style="color:${point.color}">●</span> ${point.series.name}: <b>${i18n.global.n(point.y ?? 0, 'decimal')}</b>${
                                detail ? ` · ${detail}` : ''
                            }<br/>`;
                        },
                    },
                },
            },
            series: [
                {
                    type: 'column',
                    name: config.seriesName.value,
                    data: seriesData.value,
                },
            ],
        });
    });
}
